import { EMPLOYEE_ROLE_CONFIG } from "@/constants/employees";
import type { Employee } from "@/types";
import EmployeeStatusBadge from "./EmployeeStatusBadge";

interface EmployeesTableProps {
  employees: Employee[];
  branchNames: Record<string, string>;
  onView: (employee: Employee) => void;
  onEdit: (employee: Employee) => void;
  onEditPermissions: (employee: Employee) => void;
  onToggleStatus: (employee: Employee) => void;
}

const ACTION_CLASS =
  "w-9 h-9 flex items-center justify-center rounded-btn bg-background-100 text-foreground-500 transition-colors duration-200 cursor-pointer";

export default function EmployeesTable({
  employees,
  branchNames,
  onView,
  onEdit,
  onEditPermissions,
  onToggleStatus,
}: EmployeesTableProps) {
  if (employees.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
          <i className="ri-team-line text-2xl"></i>
        </div>
        <p className="text-sm text-foreground-500">
          لا يوجد موظفون مطابقون للفلاتر الحالية
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1000px] text-right border-collapse">
        <thead>
          <tr className="text-xs text-foreground-500 bg-background-50">
            <th className="font-medium px-5 py-3 whitespace-nowrap">
              اسم الموظف
            </th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الجوال</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الفرع</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الدور</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">
              الصلاحيات
            </th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">
              حالة الحساب
            </th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">
              آخر تسجيل دخول
            </th>
            <th className="font-medium px-5 py-3 whitespace-nowrap">
              الإجراءات
            </th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => {
            const role = EMPLOYEE_ROLE_CONFIG[employee.role];
            return (
              <tr
                key={employee.id}
                className="border-t border-background-100 hover:bg-primary-50/60 transition-colors duration-200"
              >
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold">
                      {employee.name.charAt(0)}
                    </span>
                    <span className="text-sm font-semibold text-foreground-950">
                      {employee.name}
                    </span>
                  </div>
                </td>
                <td
                  className="px-3 py-3.5 text-sm text-foreground-600 whitespace-nowrap"
                  dir="ltr"
                >
                  {employee.phone}
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-700 whitespace-nowrap">
                  {branchNames[employee.branchId] ?? "غير محدد"}
                </td>
                <td className="px-3 py-3.5">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${role.className}`}
                  >
                    <i className={role.icon}></i>
                    {role.label}
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-2.5 py-1 text-xs font-semibold text-foreground-700 whitespace-nowrap">
                    <i className="ri-shield-check-line"></i>
                    {employee.permissions.length} صلاحية
                  </span>
                </td>
                <td className="px-3 py-3.5">
                  <EmployeeStatusBadge status={employee.status} />
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-500 whitespace-nowrap">
                  {employee.lastLogin}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onView(employee)}
                      className={`${ACTION_CLASS} hover:bg-secondary-200 hover:text-secondary-900`}
                      aria-label="عرض التفاصيل"
                      title="عرض التفاصيل"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(employee)}
                      className={`${ACTION_CLASS} hover:bg-primary-100 hover:text-primary-800`}
                      aria-label="تعديل البيانات"
                      title="تعديل البيانات"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditPermissions(employee)}
                      className={`${ACTION_CLASS} hover:bg-accent-100 hover:text-accent-800`}
                      aria-label="تعديل الصلاحيات"
                      title="تعديل الصلاحيات"
                    >
                      <i className="ri-shield-check-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleStatus(employee)}
                      className={`${ACTION_CLASS} ${
                        employee.status === "active"
                          ? "hover:bg-red-100 hover:text-red-600"
                          : "hover:bg-primary-100 hover:text-primary-800"
                      }`}
                      aria-label={
                        employee.status === "active"
                          ? "إيقاف الحساب"
                          : "إعادة تفعيل الحساب"
                      }
                      title={
                        employee.status === "active"
                          ? "إيقاف الحساب"
                          : "إعادة تفعيل الحساب"
                      }
                    >
                      <i
                        className={
                          employee.status === "active"
                            ? "ri-pause-circle-line"
                            : "ri-play-circle-line"
                        }
                      ></i>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}